/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
import {useKeyPress} from '@github-ui/use-key-press'
import {NestableActionMenu} from '@github-ui/nestable-action-menu'
import {ProjectIcon, TableIcon} from '@primer/octicons-react'
import {SelectPanel, type SelectPanelProps} from '@primer/react'
import type {ItemInput} from '@primer/react/lib-esm/deprecated/ActionList/List'
import type React from 'react'
import {useCallback, useMemo, useState, useEffect, Suspense, useRef} from 'react'
import {
  readInlineData,
  graphql,
  type PreloadedQuery,
  usePreloadedQuery,
  useRelayEnvironment,
  useLazyLoadQuery,
  fetchQuery,
} from 'react-relay'

import {ERRORS} from '../constants/errors'
import {HOTKEYS} from '../constants/hotkeys'
import {LABELS} from '../constants/labels'
import {useItemPickersContext} from '../contexts/ItemPickersContext'
import type {ProjectPickerProject$data, ProjectPickerProject$key} from './__generated__/ProjectPickerProject.graphql'
import type {ProjectPickerQuery, ProjectPickerQuery$data} from './__generated__/ProjectPickerQuery.graphql'
import {type ItemGroup, noMatchesItem, noResultsItem} from '../shared'
import {SharedPicker} from './SharedPicker'
import {commitAddIssuesToProjectsBulkMutation} from '../mutations/add-issues-to-projects-bulk-mutation'
import type {addIssuesToProjectsBulkMutation$data} from '../mutations/__generated__/addIssuesToProjectsBulkMutation.graphql'
import {commitAddIssuesToProjectsBulkByQueryMutation} from '../mutations/add-issues-to-projects-bulk-by-query-mutation'
import type {addIssuesToProjectsBulkByQueryMutation$data} from '../mutations/__generated__/addIssuesToProjectsBulkByQueryMutation.graphql'
import type {SharedBulkActionsItemPickerProps} from './ItemPicker'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {useItemPickerErrorFallback} from './useItemPickerErrorFallback'
import {useToastContext} from '@github-ui/toast/ToastContext'
import {debounce} from '@github/mini-throttle'
import type {Subscription} from 'relay-runtime'
import type {
  ProjectPickerClassicProject$data,
  ProjectPickerClassicProject$key,
} from './__generated__/ProjectPickerClassicProject.graphql'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'

import {noop} from '@github-ui/noop'
export type Project = ProjectPickerProject$data | ProjectPickerClassicProject$data

const recentGroup: ItemGroup = {groupId: 'recent', header: {title: 'Recent', variant: 'filled'}}
const repoGroup: ItemGroup = {groupId: 'repository', header: {title: 'Repository', variant: 'filled'}}
const orgGroup: ItemGroup = {groupId: 'organization', header: {title: 'Organization', variant: 'filled'}}
const selectedGroup: ItemGroup = {groupId: 'selected'}

export const ProjectPickerProjectFragment = graphql`
  fragment ProjectPickerProject on ProjectV2 @inline {
    id
    title
    closed
    number
    viewerCanUpdate
    __typename
  }
`

export const ProjectPickerClassicProjectFragment = graphql`
  fragment ProjectPickerClassicProject on Project @inline {
    id
    title: name
    closed
    number
    viewerCanUpdate
    columns(first: 10) {
      edges {
        node {
          id
          name
        }
      }
    }
    __typename
  }
`

export const ProjectPickerGraphqlQuery = graphql`
  query ProjectPickerQuery($owner: String!, $repo: String!, $query: String) {
    repository(owner: $owner, name: $repo) {
      projectsV2(first: 5, query: $query) {
        nodes {
          ...ProjectPickerProject
        }
      }
      recentProjects(first: 5) {
        edges {
          node {
            ...ProjectPickerProject
          }
        }
      }
      projects(first: 5, search: $query) {
        nodes {
          ...ProjectPickerClassicProject
        }
      }
      owner {
        ... on Organization {
          projectsV2(first: 5, orderBy: {field: TITLE, direction: ASC}, query: $query) {
            edges {
              node {
                ...ProjectPickerProject
              }
            }
          }
          recentProjects(first: 5) {
            edges {
              node {
                ...ProjectPickerProject
              }
            }
          }
          projects(first: 5, search: $query) {
            nodes {
              ...ProjectPickerClassicProject
            }
          }
        }
        ... on User {
          recentProjects(first: 5) {
            edges {
              node {
                ...ProjectPickerProject
              }
            }
          }
          projects(first: 5, search: $query) {
            nodes {
              ...ProjectPickerClassicProject
            }
          }
        }
      }
    }
  }
`

type ProjectPickerBaseProps = {
  pickerId: string
  readonly?: boolean
  selectedProjects: Project[]
  firstSelectedProjectTitle?: string
  onSave: (projects: Project[]) => void
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  insidePortal?: boolean
  shortcutEnabled: boolean
  /**
   * Whether to render the project picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  owner: string
  repo: string
  triggerOpen?: boolean
  shortcutHintVisible?: boolean
  includeClassicProjects?: boolean
}

export type ProjectPickerProps = ProjectPickerBaseProps & {
  projectQueryRef: PreloadedQuery<ProjectPickerQuery>
}

type ProjectPickerInternalProps = ProjectPickerBaseProps & {
  projectsData: ProjectPickerQuery$data
}

type LazyProjectPickerOnClickProps = {
  repo: string
  owner: string
  shortcutEnabled: boolean
  selectedProjects: Project[]
  firstSelectedProjectTitle: string
  onSave: (selectedProjects: Project[]) => void
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  includeClassicProjects?: boolean
}

type BulkProjectPickerProps = Omit<ProjectPickerProps, 'id' | 'onSave'> &
  Omit<SharedBulkActionsItemPickerProps, 'issuesToActOn'> & {
    issueIds: string[]
  }

export const BulkProjectPicker: React.FC<BulkProjectPickerProps> = ({
  pickerId,
  useQueryForAction,
  repositoryId,
  query,
  issueIds,
  readonly,
  selectedProjects,
  firstSelectedProjectTitle,
  projectQueryRef,
  insidePortal,
  shortcutEnabled,
  anchorElement,
  onCompleted,
  onError,
  nested = false,
  owner,
  repo,
  shortcutHintVisible = true,
}) => {
  const environment = useRelayEnvironment()
  const onSave = useCallback(
    (newSelectedProjects: Project[]) => {
      if (newSelectedProjects.length === 0) {
        return
      }
      if (useQueryForAction && repositoryId && query) {
        commitAddIssuesToProjectsBulkByQueryMutation({
          environment,
          input: {
            repositoryId,
            query,
            addToProjectV2Ids: newSelectedProjects.map(l => l.id),
          },
          onCompleted: ({updateIssuesBulkByQuery}: addIssuesToProjectsBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitAddIssuesToProjectsBulkMutation({
          environment,
          input: {
            ids: [...issueIds],
            addToProjectV2Ids: newSelectedProjects.map(l => l.id),
          },
          onCompleted: ({updateIssuesBulk}: addIssuesToProjectsBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
          onError: (error: Error) => {
            onError?.(error)
          },
        })
      }
    },
    [environment, issueIds, onCompleted, onError, query, repositoryId, useQueryForAction],
  )

  return ProjectPicker({
    pickerId, // Should be issue specific, if loaded for a specific issue :)
    readonly,
    selectedProjects,
    firstSelectedProjectTitle,
    projectQueryRef,
    insidePortal,
    shortcutEnabled,
    onSave,
    anchorElement,
    nested,
    owner,
    repo,
    shortcutHintVisible,
  })
}

export function ProjectPicker({projectQueryRef, ...rest}: ProjectPickerProps) {
  const data = usePreloadedQuery(ProjectPickerGraphqlQuery, projectQueryRef)
  return data ? <ProjectPickerInternal projectsData={data} {...rest} /> : null
}

export function LazyProjectPicker(props: ProjectPickerBaseProps) {
  const [fetchKey, setFetchKey] = useState(0)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: 'Cannot edit projects right now',
    anchorElement: props.anchorElement,
  })

  return (
    <PreloadedQueryBoundary onRetry={() => setFetchKey(fetchKey + 1)} fallback={createFallbackComponent}>
      <LazyProjectPickerInternal {...props} fetchKey={fetchKey} />
    </PreloadedQueryBoundary>
  )
}

export function LazyProjectPickerOnClick({
  repo,
  owner,
  shortcutEnabled,
  selectedProjects,
  firstSelectedProjectTitle,
  onSave,
  anchorElement,
  includeClassicProjects = false,
}: LazyProjectPickerOnClickProps) {
  const [wasTriggered, setWasTriggered] = useState(false)

  useKeyPress(shortcutEnabled ? [HOTKEYS.projectPicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered && anchorElement) {
    return anchorElement({
      onClick: () => {
        setWasTriggered(true)
      },
    })
  }

  return (
    <Suspense
      fallback={
        <SelectPanel
          loading={true}
          key="fallback"
          placeholderText={LABELS.filterProjects}
          open
          onOpenChange={noop}
          onSelectedChange={noop}
          items={[]}
          onFilterChange={noop}
          selected={undefined}
          renderAnchor={props => anchorElement(props)}
          overlayProps={{width: 'medium', height: 'auto'}}
          title={LABELS.selectProjects}
        />
      }
    >
      <LazyProjectPicker
        pickerId="list-header-projects-picker"
        repo={repo}
        owner={owner}
        shortcutEnabled={shortcutEnabled}
        onSave={onSave}
        triggerOpen={true}
        selectedProjects={selectedProjects}
        firstSelectedProjectTitle={firstSelectedProjectTitle}
        anchorElement={props => anchorElement(props)}
        includeClassicProjects={includeClassicProjects}
      />
    </Suspense>
  )
}

function LazyProjectPickerInternal({repo, owner, fetchKey, ...rest}: ProjectPickerBaseProps & {fetchKey: number}) {
  const projectsData = useLazyLoadQuery<ProjectPickerQuery>(
    ProjectPickerGraphqlQuery,
    {
      repo,
      owner,
    },
    {fetchKey},
  )
  return projectsData ? <ProjectPickerInternal projectsData={projectsData} owner={owner} repo={repo} {...rest} /> : null
}

const ProjectPickerInternal = ({
  pickerId,
  readonly,
  selectedProjects: initialSelectedProjects,
  firstSelectedProjectTitle,
  projectsData: initialProjectsData,
  onSave,
  anchorElement,
  insidePortal,
  shortcutEnabled,
  nested = false,
  owner,
  repo,
  triggerOpen = false,
  shortcutHintVisible = true,
  includeClassicProjects = false,
}: ProjectPickerInternalProps) => {
  const [projectsData, setProjectsData] = useState<ProjectPickerQuery$data>(initialProjectsData)
  const [inFlightSubscription, setInFlightSubscription] = useState<Subscription | null>(null)
  const [searchLoading, setSearchLoading] = useState<boolean>(false)
  const relayEnvironment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const {issues_react_classic_projects} = useFeatureFlags()
  const showClassicProjects = issues_react_classic_projects && includeClassicProjects

  const [filter, setFilter] = useState('')

  const fetchSearchData = useCallback(
    (searchQuery: string) => {
      setSearchLoading(true)
      const subscription = fetchQuery<ProjectPickerQuery>(relayEnvironment, ProjectPickerGraphqlQuery, {
        repo,
        owner,
        query: searchQuery,
      }).subscribe({
        next: (data: ProjectPickerQuery$data) => {
          if (data !== null) {
            setProjectsData(data)
          }
          setSearchLoading(false)
        },
        error: () => {
          setSearchLoading(false)
          addToast({
            type: 'error',
            message: ERRORS.couldNotSearchProjects,
          })
        },
      })
      setInFlightSubscription(subscription)
    },
    [addToast, owner, relayEnvironment, repo],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFetchSearchData = useCallback(
    debounce((nextValue: string) => fetchSearchData(nextValue), 200),
    [fetchSearchData],
  )

  useEffect(() => {
    if (filter.length === 0) {
      setProjectsData(initialProjectsData)
    } else {
      debounceFetchSearchData(filter)
    }
    inFlightSubscription?.unsubscribe()
    return () => {
      setSearchLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceFetchSearchData, filter, initialProjectsData])

  const recentProjects = useMemo(
    () =>
      (projectsData.repository?.owner?.recentProjects?.edges || []).flatMap(a =>
        a?.node
          ? // eslint-disable-next-line no-restricted-syntax
            [readInlineData<ProjectPickerProject$key>(ProjectPickerProjectFragment, a.node)].filter(
              item => item.closed === false,
            )
          : [],
      ),
    [projectsData],
  )

  const classicRepositoryProjects = useMemo(
    () =>
      (!showClassicProjects ? [] : projectsData.repository?.projects?.nodes || []).flatMap(a =>
        a
          ? // eslint-disable-next-line no-restricted-syntax
            [readInlineData<ProjectPickerClassicProject$key>(ProjectPickerClassicProjectFragment, a)].filter(
              item => item.closed === false && (item.columns?.edges || []).length > 0,
            )
          : [],
      ),
    [projectsData, showClassicProjects],
  )

  const memexRepositoryProjects = useMemo(
    () =>
      (projectsData.repository?.projectsV2?.nodes || []).flatMap(a =>
        a
          ? // eslint-disable-next-line no-restricted-syntax
            [readInlineData<ProjectPickerProject$key>(ProjectPickerProjectFragment, a)].filter(
              item => item.closed === false,
            )
          : [],
      ),
    [projectsData],
  )

  const repositoryProjects = useMemo(
    () => [...classicRepositoryProjects, ...memexRepositoryProjects],
    [classicRepositoryProjects, memexRepositoryProjects],
  )

  const classicOrganizationProjects = useMemo(
    () =>
      (!showClassicProjects ? [] : projectsData.repository?.owner?.projects?.nodes || []).flatMap(a =>
        a
          ? // eslint-disable-next-line no-restricted-syntax
            [readInlineData<ProjectPickerClassicProject$key>(ProjectPickerClassicProjectFragment, a)].filter(
              item => item.closed === false && (item.columns?.edges || []).length > 0,
            )
          : [],
      ),
    [projectsData, showClassicProjects],
  )

  const memexOrganizationProjects = useMemo(
    () =>
      (projectsData.repository?.owner?.projectsV2?.edges || []).flatMap(a =>
        a?.node
          ? // eslint-disable-next-line no-restricted-syntax
            [readInlineData<ProjectPickerProject$key>(ProjectPickerProjectFragment, a.node)].filter(
              item => item.closed === false,
            )
          : [],
      ),
    [projectsData],
  )

  const organizationProjects = useMemo(
    () => [...classicOrganizationProjects, ...memexOrganizationProjects],
    [classicOrganizationProjects, memexOrganizationProjects],
  )

  //
  // Presentation logic
  //

  // Array of Project IDs
  const [selected, setSelected] = useState<string[]>(initialSelectedProjects.map(p => p.id))
  // Reset the state for selected projects when the issueId changes
  useEffect(() => {
    setSelected(initialSelectedProjects.map(p => p.id))
  }, [initialSelectedProjects, pickerId])

  /// Convert a Project to an ItemInput object that SelectPanel can render
  const generateItemProps: {(groupId: string, p: Project): ItemInput} = useCallback(
    (groupId, p) => {
      const {title: text, id} = p

      const item = {
        text,
        id,
        groupId,
        selected: selected.includes(id),
        disabled: !p.viewerCanUpdate,
        leadingVisual: showClassicProjects ? (p.__typename === 'ProjectV2' ? TableIcon : ProjectIcon) : undefined,
      }
      return item
    },
    [selected, showClassicProjects],
  )

  /// Set of all projects in the picker
  const allProjects: Record<string, Project[]> = useMemo(() => {
    return {
      selected: initialSelectedProjects,
      recent: recentProjects.filter(p => !selected.includes(p.id)),
      repository: repositoryProjects.filter(p => !selected.includes(p.id)),
      organization: organizationProjects.filter(p => !selected.includes(p.id)),
    }
    //disabling this check since we don't want to auto-update when the selected projects change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedProjects, recentProjects, repositoryProjects, organizationProjects])

  const projectsToSelect = useMemo(
    () => Object.keys(allProjects).flatMap(pKey => allProjects[pKey]!.map(p => generateItemProps(pKey, p))),
    [allProjects, generateItemProps],
  )

  /// Subset of projects that have been selected
  const selectedProjects = useMemo(
    () => projectsToSelect.filter(p => p.id && selected.includes(p.id.toString())),
    [selected, projectsToSelect],
  )

  /// Optional search by filtering project title
  const filteredProjects = useMemo(() => {
    // We have nothing to filter
    if (projectsToSelect.length < 1) return [noResultsItem]

    // We have nothing to filter by
    if (filter === '') {
      return projectsToSelect
    }
    const filtered = projectsToSelect.filter(p => p.text?.toLowerCase().includes(filter.toLowerCase()))

    // We have no filter matches
    if (filtered.length < 1) return [noMatchesItem]
    return filtered
  }, [filter, projectsToSelect])

  /// Groups to render
  const groups = useMemo(
    () =>
      !filteredProjects.includes(noMatchesItem) && !filteredProjects.includes(noResultsItem)
        ? // We have results, so we can render groups
          ([
            initialSelectedProjects.length > 0 ? selectedGroup : null,
            recentProjects.length > 0 ? recentGroup : null,
            repositoryProjects.length > 0 ? repoGroup : null,
            organizationProjects.length > 0 ? orgGroup : null,
          ].filter(group => group !== null) as ItemGroup[])
        : // We have no results, so we can't render groups
          [],
    [
      filteredProjects,
      initialSelectedProjects.length,
      recentProjects.length,
      repositoryProjects.length,
      organizationProjects.length,
    ],
  )

  const handleSelectionChange = useCallback(
    (selection: ItemInput[]) => {
      // Find out if the selection now excludes a previously selected item, i.e. the diff
      const diff = selectedProjects.filter(p => !selection.includes(p)).map(p => p.id)
      let projectIds: Array<string | number> = selected

      // Handle selected projects
      const selectedIds = selection
        // Filter out items that don't have IDs
        .filter(item => item.id !== undefined)
        // Pull out the IDs and cast them
        .map(item => item.id) as Array<string | number>
      projectIds = [...projectIds, ...selectedIds]

      // Handle removed projects
      if (diff.length > 0) {
        projectIds = projectIds.filter(p => !diff.includes(p))
      }

      const projectIdsSet = new Set(projectIds.map(id => id.toString()))
      setSelected([...projectIdsSet])
    },
    [setSelected, selectedProjects, selected],
  )

  const {updateOpenState, anyItemPickerOpen} = useItemPickersContext()
  const [isOpen, setOpen] = useState(triggerOpen)
  const onOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open)
      updateOpenState(pickerId, open)
      if (!open) {
        setFilter('')
        const uniqueProjects: Project[] = selected.flatMap(
          id =>
            Object.keys(allProjects)
              .flatMap(pKey => allProjects[pKey]!)
              .find(p => p.id === id) || [],
        )
        onSave(uniqueProjects.filter(p => p !== undefined))
      }
    },
    [updateOpenState, pickerId, setFilter, selected, onSave, allProjects],
  )

  const onHotKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (!shortcutEnabled || anyItemPickerOpen() || isOpen) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
      onOpenChange(true)
    },
    [anyItemPickerOpen, isOpen, onOpenChange, shortcutEnabled],
  )

  useKeyPress([HOTKEYS.projectPicker], onHotKeyPress, {
    triggerWhenInputElementHasFocus: false,
    triggerWhenPortalIsActive: insidePortal,
  })

  const projectPickerRef = useRef<HTMLButtonElement>(null)

  const renderAnchor = useCallback(
    (props: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(props)
      }

      return (
        <SharedPicker
          anchorText={LABELS.noProjects}
          sharedPickerMainValue={firstSelectedProjectTitle}
          anchorProps={props}
          ariaLabel={LABELS.selectProjects}
          readonly={readonly}
          nested={nested}
          leadingIcon={TableIcon}
          hotKey={!readonly && shortcutEnabled && shortcutHintVisible ? HOTKEYS.projectPicker.toUpperCase() : undefined}
          ref={projectPickerRef}
        />
      )
    },
    [anchorElement, firstSelectedProjectTitle, shortcutHintVisible, nested, readonly, shortcutEnabled],
  )

  const selectPanelProps = useMemo<SelectPanelProps>(
    () => ({
      renderAnchor,
      placeholderText: LABELS.filterProjects,
      items: filteredProjects,
      selected: selectedProjects,
      open: isOpen,
      onOpenChange,
      onSelectedChange: handleSelectionChange,
      filterValue: filter,
      onFilterChange: setFilter,
      showItemDividers: true,
      overlayProps: {width: 'medium', height: filteredProjects.length <= 2 ? 'auto' : 'large'} as const,
      groupMetadata: groups && filteredProjects.length > 0 ? groups : undefined,
      'aria-label': 'Project results',
      loading: searchLoading,
      title: LABELS.selectProjects,
    }),
    [
      filter,
      filteredProjects,
      groups,
      handleSelectionChange,
      isOpen,
      onOpenChange,
      renderAnchor,
      searchLoading,
      selectedProjects,
    ],
  )

  if (nested) {
    return <NestableActionMenu.NestedSelectPanel data-testid="item-picker-root" {...selectPanelProps} />
  }

  return <SelectPanel data-testid="item-picker-root" {...selectPanelProps} anchorRef={projectPickerRef} />
}

try{ BulkProjectPicker.displayName ||= 'BulkProjectPicker' } catch {}
try{ ProjectPicker.displayName ||= 'ProjectPicker' } catch {}
try{ LazyProjectPicker.displayName ||= 'LazyProjectPicker' } catch {}
try{ LazyProjectPickerOnClick.displayName ||= 'LazyProjectPickerOnClick' } catch {}
try{ LazyProjectPickerInternal.displayName ||= 'LazyProjectPickerInternal' } catch {}
try{ ProjectPickerInternal.displayName ||= 'ProjectPickerInternal' } catch {}